import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Map, { Marker, Popup, Layer, Source, NavigationControl } from "react-map-gl/mapbox";
import { ClientOnly } from "~/components/blocks/common/client-only";
import { PropertyCard } from "./PropertyCard";
import supercluster from "supercluster";
import convertCurrency from "~/lib/helpers/currencyConverter";
import { useCurrentCurrency } from "~/lib/helpers/CurrencyHelper";

const getMapboxAccessToken = (): string => {
  return process.env.PUBLIC_REMIX_APP_MAPBOX_ACCESS_TOKEN || "";
};

const getMapboxStyle = (): string => {
  return process.env.PUBLIC_REMIX_APP_MAPBOX_STYLE || "mapbox://styles/mapbox/streets-v12";
};

type Feature = {
  count?: number;
  type: "Feature";
  properties: { title?: string; cluster?: boolean; point_count?: number; cluster_id?: number };
  geometry: { type: "Point"; coordinates: [number, number] };
  data: any;
};

type GeoJSONData = {
  type: "FeatureCollection";
  features: Feature[];
};

type ChatMarkerProps = {
  lat: number;
  long: number;
  text: string;
  onClick: () => void;
  count: number;
  isHovered: boolean;
  isFromChatAPI: boolean;
};

const ChatMarker: React.FC<ChatMarkerProps> = ({ lat, long, text, onClick, count, isHovered, isFromChatAPI }) => {
  const getMarkerClassName = () => {
    if (isHovered) {
      return "bg-black text-white border-black";
    }
    return isFromChatAPI
      ? "bg-oh-primary text-white border-oh-primary hover:bg-oh-primary/90"
      : "bg-white text-gray-800 border border-gray-300 hover:bg-black hover:text-white";
  };

  const markerZIndex = isFromChatAPI ? 2 : 1;
  const hoveredZIndex = isHovered ? 4 : markerZIndex;

  return (
    <Marker longitude={long} latitude={lat} style={{ zIndex: hoveredZIndex }}>
      <div
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className={`text-sm font-semibold px-3 py-1 rounded-2xl shadow-2xl transition-all duration-200 ease-in-out cursor-pointer relative ${getMarkerClassName()}`}
        style={{
          zIndex: hoveredZIndex,
          position: "relative",
        }}
      >
        {text}
        {count > 1 && (
          <div className="absolute top-[-10px] right-[-10px] flex items-center justify-center w-[20px] h-[20px] rounded-full bg-oh-secondary">
            <p className="text-white text-[12px]">{count}</p>
          </div>
        )}
      </div>
    </Marker>
  );
};

type ChatMapProps = {
  data: any[];
  fitData?: any[];
  region: string;
  defaultLat: number;
  defaultLng: number;
  recalibrate: boolean;
  setRecalibrate: (value: boolean) => void;
  height?: string;
  hoverId?: string;
  onHover?: (id: string) => void;
  onViewportChangeEnd?: (center: { lat: number; lng: number }) => void;
  showPerDesk?: boolean;
  shortlistedPropertyIds?: string[];
  onShortlistProperty?: (propertyId: string) => void;
};

export function ChatMap({
  data,
  fitData,
  region,
  defaultLat,
  defaultLng,
  recalibrate,
  setRecalibrate,
  height = "300px",
  hoverId = "",
  onHover,
  onViewportChangeEnd,
  showPerDesk = false,
  shortlistedPropertyIds = [],
  onShortlistProperty,
}: ChatMapProps) {
  const mapRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [currentMarker, setCurrentMarker] = useState<Feature | null>(null);
  const [unclusteredPoints, setUnclusteredPoints] = useState<Feature[]>([]);
  const { currency } = useCurrentCurrency();

  const fitTargetData = useMemo(() => (fitData && fitData.length > 0 ? fitData : data), [fitData, data]);

  const clusterData: GeoJSONData = useMemo(() => {
    return {
      type: "FeatureCollection",
      features: data.map((item) => ({
        type: "Feature",
        properties: { title: "Point" },
        geometry: {
          type: "Point",
          coordinates: [item.geolocationLong, item.geolocationLat],
        },
        data: { ...item },
      })),
    };
  }, [data]);

  const groupPointsByLocation = useCallback((points: Feature[]) => {
    const grouped: Record<string, Feature & { count: number; data: any[] }> = {};

    points.forEach((point: Feature) => {
      const coordKey = `${point.geometry.coordinates[0]},${point.geometry.coordinates[1]}`;

      if (!grouped[coordKey]) {
        grouped[coordKey] = {
          ...point,
          data: [{ data: point.data }],
          count: 1,
        };
      } else {
        grouped[coordKey].count += 1;
        grouped[coordKey].data.push({ data: point.data });
      }
    });

    return Object.values(grouped);
  }, []);

  const updateUnclusteredPoints = useCallback(() => {
    if (!mapRef.current || !mapLoaded) return;
    const map = mapRef.current.getMap();
    const bounds = map.getBounds().toArray().flat() as any;
    const expandFactor = 0.9;
    const minLng = bounds[0] - (bounds[2] - bounds[0]) * expandFactor;
    const minLat = bounds[1] - (bounds[3] - bounds[1]) * expandFactor;
    const maxLng = bounds[2] + (bounds[2] - bounds[0]) * expandFactor;
    const maxLat = bounds[3] + (bounds[3] - bounds[1]) * expandFactor;
    const paddedBounds = [minLng, minLat, maxLng, maxLat] as any;
    const zoom = map.getZoom();
    const cluster = new supercluster({ radius: 100, maxZoom: 6 });
    cluster.load(clusterData.features);
    const clusters = cluster.getClusters(paddedBounds, Math.floor(zoom));
    const individualMarkers = clusters.filter((c: any) => !c.properties?.point_count) as Feature[];
    setUnclusteredPoints(groupPointsByLocation(individualMarkers));
  }, [clusterData, mapLoaded, groupPointsByLocation]);

  const notifyViewportCenter = useCallback(() => {
    if (!mapRef.current || !onViewportChangeEnd) return;
    const center = mapRef.current.getMap().getCenter();
    onViewportChangeEnd({ lat: center.lat, lng: center.lng });
  }, [onViewportChangeEnd]);

  const fitMapToMarkers = useCallback(() => {
    if (!mapRef.current || fitTargetData.length === 0) return;
    const map = mapRef.current.getMap();

    const coordinates: [number, number][] = fitTargetData.map((p) => [p.geolocationLong, p.geolocationLat] as [number, number]);

    if (coordinates.length === 1) {
      map.flyTo({
        center: coordinates[0],
        zoom: 12,
      });
      setTimeout(() => {
        notifyViewportCenter();
      }, 1100);
      return;
    }

    const bounds = coordinates.reduce(
      (acc, coord) => {
        return [
          [Math.min(acc[0][0], coord[0]), Math.min(acc[0][1], coord[1])],
          [Math.max(acc[1][0], coord[0]), Math.max(acc[1][1], coord[1])],
        ];
      },
      [
        [Infinity, Infinity],
        [-Infinity, -Infinity],
      ],
    );

    map.fitBounds(bounds, {
      padding: 100,
      duration: 1000,
    });
    setTimeout(() => {
      notifyViewportCenter();
    }, 1100);
  }, [fitTargetData, notifyViewportCenter]);

  const handleMarkerClick = useCallback(
    (feature: Feature) => {
      if (!mapRef.current) return;
      const featureData = Array.isArray(feature.data) ? feature.data[0] : feature.data;
      const markerData = featureData?.data || featureData;
      const map = mapRef.current.getMap();

      map.easeTo({
        center: [feature.geometry.coordinates[0], feature.geometry.coordinates[1]],
        offset: [0, 140],
        duration: 400,
      });

      onHover && onHover(markerData.id || markerData.sfid || "");
      setCurrentMarker(feature);
    },
    [onHover],
  );

  useEffect(() => {
    if (recalibrate && mapLoaded) {
      // Prioritize fitTargetData (fitData override or marker data) when recalibrating.
      if (fitTargetData.length === 0) {
        const map = mapRef.current?.getMap();
        if (map) {
          map.flyTo({
            center: [defaultLng, defaultLat],
            zoom: 12,
            duration: 1000,
          });
        }
      } else {
        fitMapToMarkers();
        updateUnclusteredPoints();
      }
      setRecalibrate(false);
    }
  }, [
    recalibrate,
    mapLoaded,
    fitTargetData.length,
    defaultLat,
    defaultLng,
    fitMapToMarkers,
    updateUnclusteredPoints,
    setRecalibrate,
  ]);

  useEffect(() => {
    if (!mapRef.current || !mapLoaded) return;
    try {
      const map = mapRef.current.getMap();
      const source = map.getSource("chatMap") as any;
      if (source && source.setData) {
        source.setData(clusterData);
      }
    } catch (error) {
      console.error("Error updating map source:", error);
    }
  }, [clusterData, mapLoaded]);

  useEffect(() => {
    if (!containerRef.current || !mapLoaded) return;

    const resizeObserver = new ResizeObserver(() => {
      if (mapRef.current) {
        try {
          mapRef.current.getMap().resize();
        } catch {
          // ignore
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [mapLoaded]);

  useEffect(() => {
    if (mapLoaded && data.length > 0) {
      updateUnclusteredPoints();
    }
  }, [mapLoaded, data.length, updateUnclusteredPoints]);

  return (
    <div
      ref={containerRef}
      className="w-full rounded-lg overflow-hidden border border-gray-200 bg-white shadow-sm"
      style={{ height, minHeight: height }}
    >
      <Suspense
        fallback={
          <div className="h-full w-full flex items-center justify-center">
            <div className="text-sm text-gray-500">Loading map...</div>
          </div>
        }
      >
        <ClientOnly
          fallback={
            <div className="h-full w-full flex items-center justify-center bg-gray-100">
              <div className="text-sm text-gray-500">Loading map...</div>
            </div>
          }
        >
          {() => (
            <div className="h-full w-full">
              <Map
                ref={mapRef}
                style={{ width: "100%", height: "100%" }}
                mapStyle={getMapboxStyle()}
                mapboxAccessToken={getMapboxAccessToken()}
                attributionControl={false}
                initialViewState={{
                  latitude: defaultLat,
                  longitude: defaultLng,
                  zoom: 12,
                }}
                scrollZoom
                onLoad={() => {
                  if (fitTargetData.length > 0) {
                    updateUnclusteredPoints();
                    fitMapToMarkers();
                  }

                  if (mapRef.current) {
                    mapRef.current.on("click", "clusters", (e: { point: any }) => {
                      const features = mapRef.current.queryRenderedFeatures(e.point, {
                        layers: ["clusters"],
                      });
                      const clusterId = features[0].properties.cluster_id;
                      mapRef.current.getSource("chatMap").getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
                        if (err) return;
                        mapRef.current.easeTo({
                          center: features[0].geometry.coordinates,
                          zoom,
                        });
                      });
                    });

                    mapRef.current.on("mouseenter", "clusters", () => {
                      mapRef.current.getCanvas().style.cursor = "pointer";
                    });

                    mapRef.current.on("mouseleave", "clusters", () => {
                      mapRef.current.getCanvas().style.cursor = "";
                    });
                  }

                  setMapLoaded(true);
                  notifyViewportCenter();
                }}
                onMoveEnd={() => {
                  updateUnclusteredPoints();
                  notifyViewportCenter();
                }}
                onDragEnd={() => {
                  updateUnclusteredPoints();
                  notifyViewportCenter();
                }}
                onZoomEnd={() => {
                  updateUnclusteredPoints();
                  notifyViewportCenter();
                }}
                onClick={() => {
                  if (currentMarker) {
                    onHover && onHover("");
                    setCurrentMarker(null);
                  }
                }}
              >
                {mapLoaded && (
                  <Source id="chatMap" type="geojson" data={clusterData} cluster clusterMaxZoom={6} clusterRadius={100}>
                    <Layer
                      id="clusters"
                      type="circle"
                      filter={["has", "point_count"]}
                      paint={{
                        "circle-color": ["step", ["get", "point_count"], "#51bbd6", 100, "#f1f075", 750, "#f28cb1"],
                        "circle-radius": ["step", ["get", "point_count"], 15, 100, 20, 750, 25],
                      }}
                    />
                    <Layer
                      id="cluster-count"
                      type="symbol"
                      filter={["has", "point_count"]}
                      layout={{
                        "text-field": "{point_count_abbreviated}",
                        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
                        "text-size": 12,
                      }}
                    />
                  </Source>
                )}

                {mapLoaded &&
                  unclusteredPoints.map((feature, index) => {
                    const featureData = Array.isArray(feature.data) ? feature.data[0] : feature.data;
                    const markerData = featureData?.data || featureData;

                    const isFromChatAPI = markerData?.isFromChatAPI || false;
                    const propertyId = markerData?.id || markerData?.sfid || `marker-${index}`;
                    const isHovered = hoverId === propertyId;

                    return (
                      <ChatMarker
                        key={propertyId}
                        lat={markerData.geolocationLat}
                        long={markerData.geolocationLong}
                        text={convertCurrency(
                          showPerDesk ? markerData?.costPerWorkstation || markerData?.avgMonthlyCostPerWorkstation : markerData.priceMonthly,
                          markerData.currencyIsoCode,
                          currency,
                        )}
                        onClick={() => handleMarkerClick(feature)}
                        count={feature.count || 1}
                        isHovered={isHovered}
                        isFromChatAPI={isFromChatAPI}
                      />
                    );
                  })}

                {mapLoaded && currentMarker && (
                  <Popup
                    longitude={currentMarker.geometry.coordinates[0]}
                    latitude={currentMarker.geometry.coordinates[1]}
                    anchor="bottom"
                    onClose={() => {
                      onHover && onHover("");
                      setCurrentMarker(null);
                    }}
                    closeButton={false}
                    maxWidth="auto"
                    offset={[0, -10]}
                    className="chatmap-property-popup z-[80] rounded-lg overflow-hidden border-none bg-transparent p-0"
                  >
                    <div className="relative z-[80] w-[320px] max-w-[80vw] space-y-2">
                      {(() => {
                        const markerProperties = (Array.isArray(currentMarker.data) ? currentMarker.data : [currentMarker.data])
                          .map((item: any) => item?.data || item)
                          .filter(Boolean);

                        const property = markerProperties[0];
                        if (!property) return null;

                        const propertyId = property.id || property.sfid || property._id || property.db_id || "popup-property";
                        const canShortlist = Boolean(property.id || property.sfid || property._id || property.db_id);
                        const isShortlisted = canShortlist ? shortlistedPropertyIds.includes(String(propertyId)) : false;

                        return (
                          <>
                            {markerProperties.length > 1 && (
                              <div className="rounded-md bg-white/95 px-2 py-1 text-[11px] font-medium text-gray-600 shadow-sm">
                                Showing 1 of {markerProperties.length} properties at this location
                              </div>
                            )}
                            <PropertyCard
                              key={propertyId}
                              property={property}
                              showColView
                              onShortlist={
                                canShortlist
                                  ? () => onShortlistProperty && onShortlistProperty(String(propertyId))
                                  : undefined
                              }
                              isShortlisted={isShortlisted}
                              onMouseEnter={() => onHover && onHover(propertyId)}
                              onMouseLeave={() => onHover && onHover("")}
                            />
                          </>
                        );
                      })()}
                    </div>
                  </Popup>
                )}

                <NavigationControl position="bottom-right" />
              </Map>
            </div>
          )}
        </ClientOnly>
      </Suspense>
    </div>
  );
}

